import { useState, useEffect, useCallback } from "react";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import Alert from "../ui/alert/Alert";
import { storageService } from "../../services/storageService";
import { authService } from "../../services/authService";
import Select from "../form/Select";
import SearchableSelect from "../form/SearchableSelect";
import brazilianBanks from "../../utils/brazilianBanksList";
import { AsaasFinancialNotice } from "../common/AsaasSeal";

// Função para aplicar máscara de CNPJ: 00.000.000/0000-00
const applyMaskCnpj = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .substr(0, 18);
};

// Função para aplicar máscara de CPF: 000.000.000-00
const applyMaskCpf = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .substr(0, 14);
};

// Função para aplicar máscara de telefone: (00) 00000-0000
const applyMaskPhone = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2')
        .substr(0, 15);
};

// Função para aplicar máscara de CEP: 00000-000
const applyMaskCep = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{5})(\d)/, '$1-$2')
        .substr(0, 9);
};

// Função para validar CPF
const isValidCPF = (cpf: string): boolean => {
    // Remove caracteres não numéricos
    const strCPF = cpf.replace(/\D/g, '');

    // Verifica se tem 11 dígitos
    if (strCPF.length !== 11) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(strCPF)) return false;

    // Validação do primeiro dígito verificador
    let sum = 0;
    let remainder;

    for (let i = 1; i <= 9; i++) {
        sum = sum + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;
    if ((remainder === 10) || (remainder === 11)) remainder = 0;
    if (remainder !== parseInt(strCPF.substring(9, 10))) return false;

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum = sum + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
    }

    remainder = (sum * 10) % 11;
    if ((remainder === 10) || (remainder === 11)) remainder = 0;
    if (remainder !== parseInt(strCPF.substring(10, 11))) return false;

    return true;
};

// Função para validar CNPJ
const isValidCNPJ = (cnpj: string): boolean => {
    // Remove caracteres não numéricos
    const strCNPJ = cnpj.replace(/\D/g, '');

    // Verifica se tem 14 dígitos
    if (strCNPJ.length !== 14) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(strCNPJ)) return false;

    // Validação do primeiro dígito verificador
    let tamanho = strCNPJ.length - 2;
    let numeros = strCNPJ.substring(0, tamanho);
    const digitos = strCNPJ.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
        soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== parseInt(digitos.charAt(0))) return false;

    // Validação do segundo dígito verificador
    tamanho = tamanho + 1;
    numeros = strCNPJ.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
        soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }

    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== parseInt(digitos.charAt(1))) return false;

    return true;
};

export default function OnboardingForm() {
    // Proteção contra problemas de DOM
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    // CPF desativado temporariamente — usuário entra direto no fluxo CNPJ (step 2)
    const [currentStep, setCurrentStep] = useState(2);
    const [apiError, setApiError] = useState("");
    const [loading, setLoading] = useState(false);
    const [documentFile, setDocumentFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Estado para gerenciar alertas
    const [alert, setAlert] = useState({
        show: false,
        variant: "error",
        title: "",
        message: ""
    });


    // Estado para tipo de conta (CNPJ ou CPF)
    // CPF desativado temporariamente — forçando CNPJ como padrão
    const [accountType, setAccountType] = useState<"cnpj" | "cpf" | null>("cnpj");

    // Estado para dados de CNPJ
    const [companyData, setCompanyData] = useState({
        cnpj: "",
        companyName: "",
        companyCep: "",
        companyAddress: "",
        companyNumber: "",
        companyComplement: "",
        companyNeighborhood: "",
        companyCity: "",
        companyState: "",
        adminName: "",
        adminCpf: "",
        adminBirthDate: "",
        adminPhone: "",
        adminMotherName: "",
        documentFile: "",
        phone: "",
        termsAccepted: false,
        privacyTermsAccepted: false,
        // Situação cadastral (retornada pela BrasilAPI)
        cadastralStatusDesc: "",
        // Dados bancários
        accountType: "", // checking | savings
        bankNumber: "",
        agencyNumber: "",
        accountNumber: "",
        accountDigit: "",
    });

    // Estado para dados de CPF
    const [personData, setPersonData] = useState({
        name: "",
        cpf: "",
        birthDate: "",
        phone: "",
        motherName: "",
        personCep: "",
        personAddress: "",
        personNumber: "",
        personComplement: "",
        personNeighborhood: "",
        personCity: "",
        personState: "",
        termsAccepted: false,
        privacyTermsAccepted: false,
        // Dados bancários (CPF)
        accountType: "", // checking | savings
        bankNumber: "",
        agencyNumber: "",
        accountNumber: "",
        accountDigit: "",
    });

    // Estado para erros de validação
    const [errors, setErrors] = useState({
        accountType: "",
        cnpj: "",
        companyName: "",
        companyCep: "",
        companyAddress: "",
        companyNumber: "",
        companyComplement: "",
        companyNeighborhood: "",
        companyCity: "",
        companyState: "",
        adminName: "",
        adminCpf: "",
        adminBirthDate: "",
        adminPhone: "",
        adminMotherName: "",
        documentFile: "",
        termsAccepted: "",
        privacyTermsAccepted: "",
        name: "",
        cpf: "",
        birthDate: "",
        phone: "",
        motherName: "",
        personCep: "",
        personAddress: "",
        personNumber: "",
        personComplement: "",
        personNeighborhood: "",
        personCity: "",
        personState: "",
        adminEmail: "",
        adminPassword: "",
        email: "",
        password: "",
        // Erros de dados bancários
        bankAccountType: "",
        bankNumber: "",
        agencyNumber: "",
        accountNumber: "",
        accountDigit: "",
    });

    // Lidar com alteração do tipo de conta
    const handleAccountTypeChange = (type: "cnpj" | "cpf") => {
        setAccountType(type);
        setErrors(prev => ({ ...prev, accountType: "" }));
    };

    // Lidar com alterações nos campos de dados da empresa com máscara
    const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setCompanyData(prev => ({ ...prev, [name]: checked }));
        } else if (name === "cnpj") {
            const maskedValue = applyMaskCnpj(value);
            setCompanyData(prev => ({ ...prev, [name]: maskedValue }));

            // Limpa o erro se estiver preenchendo
            setErrors(prev => ({ ...prev, cnpj: "" }));

            // Valida o CNPJ quando completo (com a máscara, deve ter 18 caracteres)
            if (maskedValue.length === 18) {
                const isValid = isValidCNPJ(maskedValue);
                if (!isValid) {
                    setErrors(prev => ({ ...prev, cnpj: "CNPJ inválido" }));
                }
            }
        } else if (name === "adminCpf") {
            const maskedValue = applyMaskCpf(value);
            setCompanyData(prev => ({ ...prev, [name]: maskedValue }));

            // Limpa o erro se estiver preenchendo
            setErrors(prev => ({ ...prev, adminCpf: "" }));

            // Valida o CPF quando completo (com a máscara, deve ter 14 caracteres)
            if (maskedValue.length === 14) {
                const isValid = isValidCPF(maskedValue);
                if (!isValid) {
                    setErrors(prev => ({ ...prev, adminCpf: "CPF inválido" }));
                }
            }
        } else if (name === "adminPhone") {
            const maskedValue = applyMaskPhone(value);
            setCompanyData(prev => ({ ...prev, [name]: maskedValue }));
        } else if (name === "companyCep") {
            const maskedValue = applyMaskCep(value);
            setCompanyData(prev => ({ ...prev, [name]: maskedValue }));
        } else {
            setCompanyData(prev => ({ ...prev, [name]: value }));
        }

        // Limpa o erro do campo atual
        if (name !== "adminCpf" && name !== "cnpj") {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Lidar com alterações nos campos de dados pessoais com máscara
    const handlePersonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === "cpf") {
            const maskedValue = applyMaskCpf(value);
            setPersonData(prev => ({ ...prev, [name]: maskedValue }));

            // Limpa o erro se estiver preenchendo
            setErrors(prev => ({ ...prev, cpf: "" }));

            // Valida o CPF quando completo (com a máscara, deve ter 14 caracteres)
            if (maskedValue.length === 14) {
                const isValid = isValidCPF(maskedValue);
                if (!isValid) {
                    setErrors(prev => ({ ...prev, cpf: "CPF inválido" }));
                }
            }
        } else if (name === "phone") {
            const maskedValue = applyMaskPhone(value);
            setPersonData(prev => ({ ...prev, [name]: maskedValue }));
        } else {
            setPersonData(prev => ({ ...prev, [name]: value }));
        }

        // Limpa o erro do campo atual
        if (name !== "cpf") {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Lidar com upload de arquivo
    const handleDocumentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files ? e.target.files[0] : null;
        if (file) {
            setDocumentFile(file);
            setErrors(prev => ({ ...prev, documentFile: "" }));
        }
    };

    // Buscar dados da empresa pelo CNPJ
    const fetchCompanyData = async (cnpj: string) => {
        if (!cnpj || cnpj.length < 18) return; // CNPJ incompleto (considerando a máscara)

        const unmaskedCnpj = cnpj.replace(/\D/g, '');
        if (unmaskedCnpj.length !== 14) return; // CNPJ inválido

        try {
            setLoading(true);
            const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${unmaskedCnpj}`);

            if (!response.ok) {
                if (response.status === 404) {
                    setErrors(prev => ({ ...prev, cnpj: "CNPJ não encontrado, digite manualmente" }));
                } else {
                    setErrors(prev => ({ ...prev, cnpj: "Erro ao consultar CNPJ, digite manualmente" }));
                }
                setLoading(false);
                return;
            }

            const data = await response.json();

            setCompanyData(prev => ({
                ...prev,
                companyName: data.razao_social || prev.companyName,
                companyCep: data.cep ? applyMaskCep(data.cep) : prev.companyCep,
                companyAddress: data.logradouro || prev.companyAddress,
                companyNumber: data.numero || prev.companyNumber,
                companyComplement: data.complemento || prev.companyComplement,
                companyNeighborhood: data.bairro || prev.companyNeighborhood,
                companyCity: data.municipio || prev.companyCity,
                companyState: data.uf || prev.companyState,
                cadastralStatusDesc: data.descricao_situacao_cadastral || prev.cadastralStatusDesc,
                // Adiciona o nome do sócio-administrador do array qsa se disponível
                adminName: data.qsa && data.qsa.length > 0 ? data.qsa[0].nome_socio : prev.adminName
            }));

            setLoading(false);
        } catch (error) {
            console.error("Erro ao buscar dados do CNPJ:", error);
            setErrors(prev => ({ ...prev, cnpj: "Erro ao consultar CNPJ, digite manualmente" }));
            setLoading(false);
        }
    };

    // Lidar com o evento de perda de foco no campo CNPJ
    const handleCnpjBlur = () => {
        fetchCompanyData(companyData.cnpj);
    };

    // Lidar com alteração do passo
    const handleNextStep = () => {
        if (currentStep === 1 && validateStep1()) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (accountType === "cnpj" && validateStep2Cnpj()) {
                setCurrentStep(3);
            } else if (accountType === "cpf" && validateStep2Cpf()) {
                setCurrentStep(3);
            }
        } else if (currentStep === 3) {
            if (accountType === "cnpj" && validateStep3Cnpj()) {
                setCurrentStep(4);
            } else if (accountType === "cpf" && validateStep3Cpf()) {
                // Submeter o formulário para CPF (não tem próximo passo)
                handleSubmit();
            }
        } else if (currentStep === 4 && accountType === "cnpj" && validateStep4Cnpj()) {
            handleSubmit();
        }
    };

    // Lidar com alteração do passo anterior
    const handlePreviousStep = () => {
        // CPF desativado temporariamente — step mínimo é 2 (CNPJ direto)
        if (currentStep > 2) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Enviar o formulário
    const handleSubmit = useCallback(async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        try {
            setLoading(true);

            // Obter o token JWT do localStorage
            const token = localStorage.getItem('token');
            if (!token) {
                window.location.href = '/signin';
                return;
            }

            let uploadedFileUrl = "";

            // Se for CNPJ, fazer upload do arquivo antes de enviar o payload
            if (accountType === "cnpj" && documentFile) {
                setUploadProgress(0);

                // Gere um nome de arquivo único com CNPJ e timestamp
                const cleanCnpj = companyData.cnpj.replace(/\D/g, "");
                const originalFilename = documentFile.name;
                // Usa o método do storageService para padronizar
                const filename = storageService.generateUniqueFilename(
                    `company-cnpj-${cleanCnpj}-${originalFilename}`
                );

                const result = await storageService.uploadFile(
                    documentFile,
                    "companyDocuments",
                    filename
                );

                if (!result.success) {
                    throw new Error(result.error || "Falha ao fazer upload do documento");
                }
                uploadedFileUrl = result.url!;
                setUploadProgress(100);
            }

            // Construir o payload para enviar para a API
            interface OnboardingPayload {
                company: {
                    fantasyName: string;
                    legalName: string;
                    documentType: 'cnpj' | 'cpf';
                    documentNumber: string;
                    street: string;
                    number: string;
                    complement: string;
                    neighborhood: string;
                    city: string;
                    state: string;
                    zipCode: string;
                    phone: string;
                    documentUrl?: string;
                    status: 'pending';
                    bankAccount: {
                        type: string;
                        bankNumber: string;
                        agencyNumber: string;
                        accountNumber: string;
                        accountDigit: string;
                    };
                    settingsData: string;
                };
                plan: { isDefault: boolean };
                permission: { scope: string; level: string };
            }

            let payload: OnboardingPayload;

            if (accountType === 'cnpj') {
                // Verificar se o documento foi enviado
                if (!documentFile && !uploadedFileUrl) {
                    setAlert({
                        show: true,
                        variant: "error",
                        title: "Documento obrigatório",
                        message: "Por favor, faça o upload do documento da empresa para continuar."
                    });
                    setLoading(false);
                    return;
                }

                // Verificar se os campos obrigatórios do administrador estão preenchidos
                if (!companyData.adminName || !companyData.adminCpf || !companyData.adminBirthDate || !companyData.adminMotherName) {
                    setAlert({
                        show: true,
                        variant: "error",
                        title: "Dados incompletos",
                        message: "Preencha todos os dados do administrador para continuar."
                    });
                    setLoading(false);
                    return;
                }

                // Verificar se os termos foram aceitos
                if (!companyData.termsAccepted || !companyData.privacyTermsAccepted) {
                    setAlert({
                        show: true,
                        variant: "error",
                        title: "Termos não aceitos",
                        message: "É necessário aceitar os termos de uso e política de privacidade para continuar."
                    });
                    setLoading(false);
                    return;
                }

                payload = {
                    company: {
                        fantasyName: companyData.companyName,
                        legalName: companyData.companyName,
                        documentType: 'cnpj',
                        documentNumber: companyData.cnpj.replace(/\D/g, ''),
                        street: companyData.companyAddress,
                        number: companyData.companyNumber,
                        complement: companyData.companyComplement,
                        neighborhood: companyData.companyNeighborhood,
                        city: companyData.companyCity,
                        state: companyData.companyState,
                        zipCode: companyData.companyCep.replace(/\D/g, ''),
                        phone: (companyData.adminPhone || companyData.phone || '').replace(/\D/g, ''),
                        documentUrl: uploadedFileUrl,
                        status: 'pending',
                        // Dados bancários da empresa
                        bankAccount: {
                            type: companyData.accountType, // 'checking' | 'savings'
                            bankNumber: (companyData.bankNumber || '').replace(/\D/g, ''),
                            agencyNumber: (companyData.agencyNumber || '').replace(/\D/g, ''),
                            accountNumber: (companyData.accountNumber || '').replace(/\D/g, ''),
                            accountDigit: (companyData.accountDigit || '').replace(/\D/g, ''),
                        },
                        // Adicionando os dados do administrador em settingsData
                        settingsData: JSON.stringify({
                            legalRepresentative: companyData.adminName,
                            legalRepresentativeDocument: companyData.adminCpf.replace(/\D/g, ''),
                            legalRepresentativeBirthDate: companyData.adminBirthDate,
                            legalRepresentativeMotherName: companyData.adminMotherName,
                            legalRepresentativePhone: (companyData.adminPhone || '').replace(/\D/g, '')
                        })
                    },
                    plan: {
                        isDefault: true
                    },
                    permission: {
                        scope: 'company',
                        level: 'admin'
                    }
                };
            } else {
                // Verificar se os campos obrigatórios estão preenchidos
                if (!personData.name || !personData.cpf || !personData.birthDate || !personData.motherName) {
                    setAlert({
                        show: true,
                        variant: "error",
                        title: "Dados incompletos",
                        message: "Preencha todos os dados obrigatórios para continuar."
                    });
                    setLoading(false);
                    return;
                }

                // Verificar se os termos foram aceitos
                if (!personData.termsAccepted || !personData.privacyTermsAccepted) {
                    setAlert({
                        show: true,
                        variant: "error",
                        title: "Termos não aceitos",
                        message: "É necessário aceitar os termos de uso e política de privacidade para continuar."
                    });
                    setLoading(false);
                    return;
                }

                // Payload para pessoa física (CPF)
                payload = {
                    company: {
                        fantasyName: personData.name,
                        legalName: personData.name,
                        documentType: 'cpf',
                        documentNumber: personData.cpf.replace(/\D/g, ''),
                        street: personData.personAddress,
                        number: personData.personNumber,
                        complement: personData.personComplement,
                        neighborhood: personData.personNeighborhood,
                        city: personData.personCity,
                        state: personData.personState,
                        zipCode: personData.personCep.replace(/\D/g, ''),
                        phone: (personData.phone || '').replace(/\D/g, ''),
                        status: 'pending',
                        // Dados bancários (CPF)
                        bankAccount: {
                            type: personData.accountType, // 'checking' | 'savings'
                            bankNumber: (personData.bankNumber || '').replace(/\D/g, ''),
                            agencyNumber: (personData.agencyNumber || '').replace(/\D/g, ''),
                            accountNumber: (personData.accountNumber || '').replace(/\D/g, ''),
                            accountDigit: (personData.accountDigit || '').replace(/\D/g, ''),
                        },
                        // Adicionando os dados do administrador em settingsData
                        // No caso de pessoa física, os dados do administrador são os mesmos da pessoa
                        settingsData: JSON.stringify({
                            legalRepresentative: personData.name,
                            legalRepresentativeDocument: personData.cpf.replace(/\D/g, ''),
                            legalRepresentativeBirthDate: personData.birthDate,
                            legalRepresentativeMotherName: personData.motherName,
                            legalRepresentativePhone: (personData.phone || '').replace(/\D/g, '')
                        })
                    },
                    plan: {
                        isDefault: true
                    },
                    permission: {
                        scope: 'company',
                        level: 'admin'
                    }
                };
            }

            // Enviar os dados para a API com token no header
            const apiUrl = `${import.meta.env.VITE_API_URL}/api/onboarding`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Erro ao processar o cadastro');
            }

            // Salvar o novo companyId no localStorage
            if (data.data && data.data.companyId) {
                localStorage.setItem('companyId', data.data.companyId);

                // Atualizamos também o objeto user para manter consistência:
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                user.companyId = data.data.companyId;
                user.hasCompany = true;
                localStorage.setItem('user', JSON.stringify(user));
            }

            // Aguardar alguns ms para garantir persistência
            await new Promise(res => setTimeout(res, 500));

            try {
                // Renovar o token JWT antes de redirecionar usando o authService
                const renewed = await authService.renewToken();
                if (renewed) {
                    console.log('Token renovado com sucesso');
                } else {
                    console.warn('Não foi possível renovar o token, mas continuando com redirecionamento');
                }
            } catch (renewError) {
                console.warn('Erro ao renovar token:', renewError);
                // Continuar com redirecionamento mesmo se falhar a renovação
            }

            // Redirecionar para a dashboard após sucesso
            window.location.href = '/dashboard';

        } catch (error) {
            console.error('Erro ao enviar dados:', error);
            setApiError(error instanceof Error ? error.message : 'Erro ao enviar dados');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [accountType, companyData, personData, documentFile]);


    // Validar o primeiro passo
    const validateStep1 = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!accountType) {
            newErrors.accountType = "Por favor, selecione uma opção";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validar o segundo passo - CNPJ (dados da empresa)
    const validateStep2Cnpj = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!companyData.cnpj) {
            newErrors.cnpj = "CNPJ é obrigatório";
            isValid = false;
        }

        if (!companyData.companyName) {
            newErrors.companyName = "Razão Social é obrigatória";
            isValid = false;
        }

        if (!companyData.companyCep) {
            newErrors.companyCep = "CEP da empresa é obrigatório";
            isValid = false;
        }

        if (!companyData.companyAddress) {
            newErrors.companyAddress = "Endereço é obrigatório";
            isValid = false;
        }

        if (!companyData.companyNumber) {
            newErrors.companyNumber = "Número é obrigatório";
            isValid = false;
        }

        if (!companyData.companyNeighborhood) {
            newErrors.companyNeighborhood = "Bairro é obrigatório";
            isValid = false;
        }

        if (!companyData.companyCity) {
            newErrors.companyCity = "Cidade é obrigatória";
            isValid = false;
        }

        if (!companyData.companyState) {
            newErrors.companyState = "Estado é obrigatório";
            isValid = false;
        }

        // Bloquear prosseguimento se a empresa estiver BAIXADA
        if ((companyData.cadastralStatusDesc || '').toUpperCase() === 'BAIXADA') {
            newErrors.cnpj = "Empresa com situação cadastral BAIXADA não pode se cadastrar";
            isValid = false;
        }

        // Bloquear CNPJ da Sellyo
        const blockedCnpj = '60478084000103';
        const unmaskedCnpj = (companyData.cnpj || '').replace(/\D/g, '');
        if (unmaskedCnpj === blockedCnpj) {
            newErrors.cnpj = 'Este CNPJ não pode ser utilizado para cadastro.';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validar o terceiro passo - CNPJ (dados do administrador da empresa)
    const validateStep3Cnpj = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!companyData.adminName) {
            newErrors.adminName = "Nome do administrador é obrigatório";
            isValid = false;
        }

        if (!companyData.adminCpf) {
            newErrors.adminCpf = "CPF do administrador é obrigatório";
            isValid = false;
        }

        if (!companyData.adminBirthDate) {
            newErrors.adminBirthDate = "Data de nascimento é obrigatória";
            isValid = false;
        }

        if (!companyData.adminPhone) {
            newErrors.adminPhone = "Celular é obrigatório";
            isValid = false;
        }

        if (!companyData.adminMotherName) {
            newErrors.adminMotherName = "Nome da mãe é obrigatório";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validar o quarto passo - CNPJ (documentos da empresa)
    const validateStep4Cnpj = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!documentFile) {
            newErrors.documentFile = "Upload do contrato social é obrigatório";
            isValid = false;
        }

        if (!companyData.termsAccepted) {
            newErrors.termsAccepted = "Você precisa confirmar esta declaração";
            isValid = false;
        }

        if (!companyData.privacyTermsAccepted) {
            newErrors.privacyTermsAccepted = "Você precisa confirmar esta declaração";
            isValid = false;
        }

        // Validações de Dados Bancários
        if (!companyData.accountType) {
            newErrors.bankAccountType = "Tipo de conta é obrigatório";
            isValid = false;
        }
        if (!companyData.bankNumber) {
            newErrors.bankNumber = "Selecione um banco";
            isValid = false;
        }
        if (!companyData.agencyNumber) {
            newErrors.agencyNumber = "Número da agência é obrigatório";
            isValid = false;
        }
        if (!companyData.accountNumber) {
            newErrors.accountNumber = "Número da conta é obrigatório";
            isValid = false;
        }
        if (!companyData.accountDigit) {
            newErrors.accountDigit = "Dígito da conta é obrigatório";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validar o segundo passo - CPF
    const validateStep2Cpf = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!personData.name) {
            newErrors.name = "Nome completo é obrigatório";
            isValid = false;
        }

        if (!personData.cpf) {
            newErrors.cpf = "CPF é obrigatório";
            isValid = false;
        }

        if (!personData.birthDate) {
            newErrors.birthDate = "Data de nascimento é obrigatória";
            isValid = false;
        }

        if (!personData.phone) {
            newErrors.phone = "Celular é obrigatório";
            isValid = false;
        }

        if (!personData.motherName) {
            newErrors.motherName = "Nome da mãe é obrigatório";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validar o terceiro passo - CPF (endereço)
    const validateStep3Cpf = () => {
        const newErrors = { ...errors };
        let isValid = true;

        if (!personData.personCep) {
            newErrors.personCep = "CEP é obrigatório";
            isValid = false;
        }

        if (!personData.personAddress) {
            newErrors.personAddress = "Endereço é obrigatório";
            isValid = false;
        }

        if (!personData.personNumber) {
            newErrors.personNumber = "Número é obrigatório";
            isValid = false;
        }

        if (!personData.personNeighborhood) {
            newErrors.personNeighborhood = "Bairro é obrigatório";
            isValid = false;
        }

        if (!personData.personCity) {
            newErrors.personCity = "Cidade é obrigatória";
            isValid = false;
        }

        if (!personData.personState) {
            newErrors.personState = "Estado é obrigatório";
            isValid = false;
        }

        if (!personData.termsAccepted) {
            newErrors.termsAccepted = "Você precisa confirmar esta declaração";
            isValid = false;
        }

        if (!personData.privacyTermsAccepted) {
            newErrors.privacyTermsAccepted = "Você precisa aceitar os termos de uso";
            isValid = false;
        }

        // Validações de Dados Bancários (agora no passo 3)
        if (!personData.accountType) {
            newErrors.bankAccountType = "Tipo de conta é obrigatório";
            isValid = false;
        }
        if (!personData.bankNumber) {
            newErrors.bankNumber = "Selecione um banco";
            isValid = false;
        }
        if (!personData.agencyNumber) {
            newErrors.agencyNumber = "Número da agência é obrigatório";
            isValid = false;
        }
        if (!personData.accountNumber) {
            newErrors.accountNumber = "Número da conta é obrigatório";
            isValid = false;
        }
        if (!personData.accountDigit) {
            newErrors.accountDigit = "Dígito da conta é obrigatório";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Função para buscar endereço por CEP (pessoa física)
    const fetchAddressByCep = async (cep: string) => {
        // Remove caracteres não numéricos
        const unmaskedCep = cep.replace(/\D/g, '');
        if (unmaskedCep.length !== 8) return; // CEP inválido

        try {
            setLoading(true);
            const response = await fetch(`https://viacep.com.br/ws/${unmaskedCep}/json/`);

            if (!response.ok) {
                setErrors(prev => ({ ...prev, personCep: "Erro ao consultar CEP" }));
                setLoading(false);
                return;
            }

            const data = await response.json();

            // Verifica se a API retornou erro
            if (data.erro) {
                setErrors(prev => ({ ...prev, personCep: "CEP não encontrado" }));
                setLoading(false);
                return;
            }

            setPersonData(prev => ({
                ...prev,
                personCep: data.cep || prev.personCep,
                personAddress: data.logradouro || prev.personAddress,
                personComplement: data.complemento || prev.personComplement,
                personNeighborhood: data.bairro || prev.personNeighborhood,
                personCity: data.localidade || prev.personCity,
                personState: data.uf || prev.personState
            }));

            setLoading(false);
        } catch (error) {
            console.error("Erro ao buscar endereço:", error);
            setErrors(prev => ({ ...prev, personCep: "Erro ao consultar CEP" }));
            setLoading(false);
        }
    };

    // Lidar com o evento de perda de foco no campo CEP da pessoa física
    const handlePersonCepBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        fetchAddressByCep(e.target.value);
    };

    // Renderizar o primeiro passo
    const renderStep1 = () => {
        return (
            <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-800 dark:text-white/90">
                    Para onde deveremos enviar seus saques?
                </h2>

                <div className="space-y-4">
                    <div className="flex items-center">
                        <input
                            id="cnpj-option"
                            name="account-type"
                            type="radio"
                            checked={accountType === "cnpj"}
                            onChange={() => handleAccountTypeChange("cnpj")}
                            className="w-5 h-5 text-brand-600 border-gray-300 focus:ring-brand-500"
                        />
                        <label
                            htmlFor="cnpj-option"
                            className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Possuo CNPJ e quero receber na minha empresa
                        </label>
                    </div>

                    <div className="flex items-center">
                        <input
                            id="cpf-option"
                            name="account-type"
                            type="radio"
                            checked={accountType === "cpf"}
                            onChange={() => handleAccountTypeChange("cpf")}
                            className="w-5 h-5 text-brand-600 border-gray-300 focus:ring-brand-500"
                        />
                        <label
                            htmlFor="cpf-option"
                            className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Quero receber na minha conta pessoa física (CPF)
                        </label>
                    </div>

                    {errors.accountType && (
                        <p className="mt-1 text-sm text-error-500">{errors.accountType}</p>
                    )}
                </div>

                <div className="pt-4">
                    <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                        Continuar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o segundo passo para CNPJ (dados da empresa)
    const renderStep2Cnpj = () => {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-4">
                        Dados da empresa
                    </h2>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="cnpj">
                                CNPJ<span className="text-error-500">*</span>
                            </Label>
                            <Input
                                type="text"
                                id="cnpj"
                                name="cnpj"
                                value={companyData.cnpj}
                                onChange={handleCompanyChange}
                                onBlur={handleCnpjBlur}
                                placeholder="00.000.000/0000-00"
                                className={errors.cnpj ? "border-error-500" : ""}
                                maxLength={18}
                            />
                            {errors.cnpj && (
                                <p className="mt-1 text-sm text-error-500">{errors.cnpj}</p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="companyName">
                                Razão Social<span className="text-error-500">*</span>
                            </Label>
                            <Input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={companyData.companyName}
                                onChange={handleCompanyChange}
                                placeholder="Razão social completa da empresa"
                                className={errors.companyName ? "border-error-500" : ""}
                                disabled={loading}
                            />
                            {errors.companyName && (
                                <p className="mt-1 text-sm text-error-500">{errors.companyName}</p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="companyCep">
                                CEP<span className="text-error-500">*</span>
                            </Label>
                            <Input
                                type="text"
                                id="companyCep"
                                name="companyCep"
                                value={companyData.companyCep}
                                onChange={handleCompanyChange}
                                placeholder="00000-000"
                                className={errors.companyCep ? "border-error-500" : ""}
                                maxLength={9}
                            />
                            {errors.companyCep && (
                                <p className="mt-1 text-sm text-error-500">{errors.companyCep}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-8">
                                <Label htmlFor="companyAddress">
                                    Endereço<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="companyAddress"
                                    name="companyAddress"
                                    value={companyData.companyAddress}
                                    onChange={handleCompanyChange}
                                    placeholder="Endereço"
                                    className={errors.companyAddress ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyAddress && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyAddress}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="companyNumber">
                                    Número<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="companyNumber"
                                    name="companyNumber"
                                    value={companyData.companyNumber}
                                    onChange={handleCompanyChange}
                                    placeholder="Número"
                                    className={errors.companyNumber ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyNumber && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyNumber}</p>
                                )}
                            </div>
                            <div className="md:col-span-12">
                                <Label htmlFor="companyComplement">
                                    Complemento
                                </Label>
                                <Input
                                    type="text"
                                    id="companyComplement"
                                    name="companyComplement"
                                    value={companyData.companyComplement}
                                    onChange={handleCompanyChange}
                                    placeholder="Complemento"
                                    className={errors.companyComplement ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyComplement && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyComplement}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="companyNeighborhood">
                                    Bairro<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="companyNeighborhood"
                                    name="companyNeighborhood"
                                    value={companyData.companyNeighborhood}
                                    onChange={handleCompanyChange}
                                    placeholder="Bairro"
                                    className={errors.companyNeighborhood ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyNeighborhood && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyNeighborhood}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="companyCity">
                                    Cidade<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="companyCity"
                                    name="companyCity"
                                    value={companyData.companyCity}
                                    onChange={handleCompanyChange}
                                    placeholder="Cidade"
                                    className={errors.companyCity ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyCity && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyCity}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="companyState">
                                    Estado<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="companyState"
                                    name="companyState"
                                    value={companyData.companyState}
                                    onChange={handleCompanyChange}
                                    placeholder="Estado"
                                    className={errors.companyState ? "border-error-500" : ""}
                                    disabled={loading}
                                />
                                {errors.companyState && (
                                    <p className="mt-1 text-sm text-error-500">{errors.companyState}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {(companyData.cadastralStatusDesc && companyData.cadastralStatusDesc.toUpperCase() === 'BAIXADA') && (
                    <div className="p-4 mt-4 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800/30">
                        <p className="text-sm text-red-700 dark:text-red-400">
                            Empresa com situação cadastral BAIXADA não pode se cadastrar.
                        </p>
                    </div>
                )}

                {/* Alerta vermelho para CNPJ bloqueado */}
                {((companyData.cnpj || '').replace(/\D/g, '') === '60478084000103') && (
                    <div className="p-4 mt-4 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800/30">
                        <p className="text-sm text-red-700 dark:text-red-400">
                            Este CNPJ não pode ser utilizado para cadastro.
                        </p>
                    </div>
                )}

                <div className="p-4 mt-4 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800/30">
                    <p className="text-sm text-yellow-700 dark:text-yellow-500">
                        Atenção: Você não pode alterar esses dados depois
                    </p>
                </div>

                <div className="flex gap-4 pt-4">
                    {/* CPF desativado temporariamente — botão Voltar oculto pois não há step anterior */}
                    {/* <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 transition bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        Voltar
                    </button> */}
                    <button
                        type="button"
                        onClick={handleNextStep}
                        disabled={(companyData.cadastralStatusDesc || '').toUpperCase() === 'BAIXADA' || ((companyData.cnpj || '').replace(/\D/g, '') === '60478084000103')}
                        className={`flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg shadow-theme-xs ${(companyData.cadastralStatusDesc || '').toUpperCase() === 'BAIXADA' || ((companyData.cnpj || '').replace(/\D/g, '') === '60478084000103')
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-brand-500 hover:bg-brand-600'
                            }`}
                    >
                        Continuar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o terceiro passo para CNPJ (dados do administrador)
    const renderStep3Cnpj = () => {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-4">
                        Dados do administrador da empresa
                    </h2>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="adminName">
                                Nome completo do administador<span className="text-error-500">*</span>
                            </Label>
                            <Input
                                type="text"
                                id="adminName"
                                name="adminName"
                                value={companyData.adminName}
                                onChange={handleCompanyChange}
                                placeholder="Nome completo do administrador"
                                className={errors.adminName ? "border-error-500" : ""}
                            />
                            {errors.adminName && (
                                <p className="mt-1 text-sm text-error-500">{errors.adminName}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="adminCpf">
                                    CPF<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="adminCpf"
                                    name="adminCpf"
                                    value={companyData.adminCpf}
                                    onChange={handleCompanyChange}
                                    placeholder="000.000.000-00"
                                    className={errors.adminCpf ? "border-error-500" : ""}
                                    maxLength={14}
                                />
                                {errors.adminCpf && (
                                    <p className="mt-1 text-sm text-error-500">{errors.adminCpf}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="adminBirthDate">
                                    Data de nascimento<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="date"
                                    id="adminBirthDate"
                                    name="adminBirthDate"
                                    value={companyData.adminBirthDate}
                                    onChange={handleCompanyChange}
                                    className={errors.adminBirthDate ? "border-error-500" : ""}
                                />
                                {errors.adminBirthDate && (
                                    <p className="mt-1 text-sm text-error-500">{errors.adminBirthDate}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="adminPhone">
                                    Celular<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="adminPhone"
                                    name="adminPhone"
                                    value={companyData.adminPhone}
                                    onChange={handleCompanyChange}
                                    placeholder="(00) 00000-0000"
                                    className={errors.adminPhone ? "border-error-500" : ""}
                                    maxLength={15}
                                />
                                {errors.adminPhone && (
                                    <p className="mt-1 text-sm text-error-500">{errors.adminPhone}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="adminMotherName">
                                    Nome completo da mãe<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="adminMotherName"
                                    name="adminMotherName"
                                    value={companyData.adminMotherName}
                                    onChange={handleCompanyChange}
                                    placeholder="Nome completo da mãe"
                                    className={errors.adminMotherName ? "border-error-500" : ""}
                                />
                                {errors.adminMotherName && (
                                    <p className="mt-1 text-sm text-error-500">{errors.adminMotherName}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 mt-4 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800/30">
                    <p className="text-sm text-yellow-700 dark:text-yellow-500">
                        Atenção: Você não pode alterar esses dados depois
                    </p>
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 transition bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                        Continuar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o quarto passo para CNPJ (documentos da empresa)
    const renderStep4Cnpj = () => {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-2">
                        Dados Bancários
                    </h2>

                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            A conta deve ter a mesma titularidade do CNPJ informado.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="accountType">
                                    Tipo de Conta<span className="text-error-500">*</span>
                                </Label>
                                <Select
                                    options={[
                                        { value: "checking", label: "Corrente" },
                                        { value: "savings", label: "Poupança" },
                                    ]}
                                    placeholder="Tipo de conta"
                                    value={companyData.accountType}
                                    onChange={(value) => {
                                        setCompanyData((prev) => ({ ...prev, accountType: value }));
                                        setErrors((prev) => ({ ...prev, bankAccountType: "" }));
                                    }}
                                    className={errors.bankAccountType ? "border-error-500" : ""}
                                />
                                {errors.bankAccountType && (
                                    <p className="mt-1 text-sm text-error-500">{errors.bankAccountType}</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="bankNumber">
                                    Banco<span className="text-error-500">*</span>
                                </Label>
                                <SearchableSelect
                                    options={brazilianBanks.map(bank => ({
                                        value: bank.code,
                                        label: `${bank.code} - ${bank.name}`
                                    }))}
                                    value={companyData.bankNumber}
                                    onChange={(value) => {
                                        setCompanyData(prev => ({ ...prev, bankNumber: value }));
                                        if (errors.bankNumber) {
                                            setErrors(prev => ({ ...prev, bankNumber: "" }));
                                        }
                                    }}
                                    placeholder="Selecione o banco"
                                    searchPlaceholder="Pesquisar banco..."
                                    error={!!errors.bankNumber}
                                />
                                {errors.bankNumber && (
                                    <p className="mt-1 text-sm text-error-500">{errors.bankNumber}</p>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label htmlFor="agencyNumber">
                                    Número da Agência<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="agencyNumber"
                                    name="agencyNumber"
                                    value={companyData.agencyNumber}
                                    onChange={handleCompanyChange}
                                    placeholder="Agência"
                                    className={errors.agencyNumber ? "border-error-500" : ""}
                                />
                                {errors.agencyNumber && (
                                    <p className="mt-1 text-sm text-error-500">{errors.agencyNumber}</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="accountNumber">
                                    Número da Conta<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="accountNumber"
                                    name="accountNumber"
                                    value={companyData.accountNumber}
                                    onChange={handleCompanyChange}
                                    placeholder="Conta"
                                    className={errors.accountNumber ? "border-error-500" : ""}
                                />
                                {errors.accountNumber && (
                                    <p className="mt-1 text-sm text-error-500">{errors.accountNumber}</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="accountDigit">
                                    Dígito da Conta<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="accountDigit"
                                    name="accountDigit"
                                    value={companyData.accountDigit}
                                    onChange={handleCompanyChange}
                                    placeholder="Dígito"
                                    className={errors.accountDigit ? "border-error-500" : ""}
                                />
                                {errors.accountDigit && (
                                    <p className="mt-1 text-sm text-error-500">{errors.accountDigit}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-2">
                        Documento da empresa
                    </h2>

                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Por favor faça o upload do contrato social atualizado da empresa (ou comprovante de MEI).
                        </p>

                        <div className="mt-2">
                            <label className="block px-4 py-3 text-sm font-medium text-gray-700 border border-gray-300 border-dashed rounded-md cursor-pointer hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800/20 dark:text-gray-300">
                                <input
                                    type="file"
                                    className="hidden"
                                    onChange={handleDocumentFileChange}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                <span className="flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    {loading && uploadProgress > 0 && uploadProgress < 100 ? (
                                        <span>
                                            Enviando arquivo: {uploadProgress}%
                                        </span>
                                    ) : documentFile ? (
                                        <span>
                                            {documentFile.name}
                                        </span>
                                    ) : (
                                        "Clique para fazer upload"
                                    )}
                                </span>
                            </label>
                            {errors.documentFile && (
                                <p className="mt-1 text-sm text-error-500">{errors.documentFile}</p>
                            )}
                        </div>

                        <div className="flex items-start gap-3 mt-4">
                            <Checkbox
                                className="w-5 h-5 mt-1"
                                checked={companyData.termsAccepted}
                                onChange={(checked) => {
                                    setCompanyData(prev => ({ ...prev, termsAccepted: checked }));
                                    setErrors(prev => ({ ...prev, termsAccepted: "" }));
                                }}
                            />
                            <div>
                                <p className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                    Confirmo que o responsável acima está autorizado(a) a representar a empresa conforme o contrato social.
                                </p>
                                {errors.termsAccepted && (
                                    <p className="mt-1 text-sm text-error-500">{errors.termsAccepted}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-start gap-3 mt-4">
                            <Checkbox
                                className="w-5 h-5 mt-1"
                                checked={companyData.privacyTermsAccepted}
                                onChange={(checked) => {
                                    setCompanyData(prev => ({ ...prev, privacyTermsAccepted: checked }));
                                    setErrors(prev => ({ ...prev, privacyTermsAccepted: "" }));
                                }}
                            />
                            <div>
                                <p className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                    Li e aceito os termos de uso, termos de licença de uso de software & a política de conteúdo da Sellyo.
                                </p>
                                {errors.privacyTermsAccepted && (
                                    <p className="mt-1 text-sm text-error-500">{errors.privacyTermsAccepted}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 mt-4 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800/30">
                    <p className="text-sm text-yellow-700 dark:text-yellow-500">
                        Atenção: Você não pode alterar esse documento depois
                    </p>
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 transition bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                        Finalizar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o segundo passo para CPF
    const renderStep2Cpf = () => {
        return (
            <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-4">
                    Seus dados pessoais
                </h2>

                <div className="space-y-4">
                    <div>
                        <Label htmlFor="name">
                            Nome completo<span className="text-error-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={personData.name}
                            onChange={handlePersonChange}
                            placeholder="Seu nome completo"
                            className={errors.name ? "border-error-500" : ""}
                        />
                        {errors.name && (
                            <p className="mt-1 text-sm text-error-500">{errors.name}</p>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="cpf">
                            CPF<span className="text-error-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="cpf"
                            name="cpf"
                            value={personData.cpf}
                            onChange={(e) => {
                                const maskedValue = applyMaskCpf(e.target.value);
                                setPersonData(prev => ({ ...prev, cpf: maskedValue }));
                                setErrors(prev => ({ ...prev, cpf: "" }));
                            }}
                            placeholder="000.000.000-00"
                            className={errors.cpf ? "border-error-500" : ""}
                            maxLength={14}
                        />
                        {errors.cpf && (
                            <p className="mt-1 text-sm text-error-500">{errors.cpf}</p>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="birthDate">
                            Data de nascimento<span className="text-error-500">*</span>
                        </Label>
                        <Input
                            type="date"
                            id="birthDate"
                            name="birthDate"
                            value={personData.birthDate}
                            onChange={handlePersonChange}
                            className={errors.birthDate ? "border-error-500" : ""}
                        />
                        {errors.birthDate && (
                            <p className="mt-1 text-sm text-error-500">{errors.birthDate}</p>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="phone">
                            Celular<span className="text-error-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="phone"
                            name="phone"
                            value={personData.phone}
                            onChange={handlePersonChange}
                            placeholder="(00) 00000-0000"
                            className={errors.phone ? "border-error-500" : ""}
                            maxLength={15}
                        />
                        {errors.phone && (
                            <p className="mt-1 text-sm text-error-500">{errors.phone}</p>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="motherName">
                            Nome completo da mãe<span className="text-error-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="motherName"
                            name="motherName"
                            value={personData.motherName}
                            onChange={handlePersonChange}
                            placeholder="Nome completo da sua mãe"
                            className={errors.motherName ? "border-error-500" : ""}
                        />
                        {errors.motherName && (
                            <p className="mt-1 text-sm text-error-500">{errors.motherName}</p>
                        )}
                    </div>
                </div>

                <div className="p-4 mt-4 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800/30">
                    <p className="text-sm text-yellow-700 dark:text-yellow-500">
                        Atenção: Você não pode alterar esses dados depois
                    </p>
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 transition bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                        Continuar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o terceiro passo para CPF (endereço)
    const renderStep3Cpf = () => {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-4">
                        Endereço
                    </h2>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="personCep">
                                CEP<span className="text-error-500">*</span>
                            </Label>
                            <Input
                                type="text"
                                id="personCep"
                                name="personCep"
                                value={personData.personCep}
                                onChange={(e) => {
                                    const maskedValue = applyMaskCep(e.target.value);
                                    setPersonData(prev => ({ ...prev, personCep: maskedValue }));
                                    setErrors(prev => ({ ...prev, personCep: "" }));
                                }}
                                onBlur={handlePersonCepBlur}
                                placeholder="00000-000"
                                className={errors.personCep ? "border-error-500" : ""}
                                maxLength={9}
                            />
                            {errors.personCep && (
                                <p className="mt-1 text-sm text-error-500">{errors.personCep}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-8">
                                <Label htmlFor="personAddress">
                                    Endereço<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="personAddress"
                                    name="personAddress"
                                    value={personData.personAddress}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personAddress: e.target.value }));
                                        setErrors(prev => ({ ...prev, personAddress: "" }));
                                    }}
                                    placeholder="Endereço"
                                    className={errors.personAddress ? "border-error-500" : ""}
                                />
                                {errors.personAddress && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personAddress}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="personNumber">
                                    Número<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="personNumber"
                                    name="personNumber"
                                    value={personData.personNumber}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personNumber: e.target.value }));
                                        setErrors(prev => ({ ...prev, personNumber: "" }));
                                    }}
                                    placeholder="Número"
                                    className={errors.personNumber ? "border-error-500" : ""}
                                />
                                {errors.personNumber && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personNumber}</p>
                                )}
                            </div>
                            <div className="md:col-span-12">
                                <Label htmlFor="personComplement">
                                    Complemento
                                </Label>
                                <Input
                                    type="text"
                                    id="personComplement"
                                    name="personComplement"
                                    value={personData.personComplement}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personComplement: e.target.value }));
                                        setErrors(prev => ({ ...prev, personComplement: "" }));
                                    }}
                                    placeholder="Complemento"
                                    className={errors.personComplement ? "border-error-500" : ""}
                                />
                                {errors.personComplement && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personComplement}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="personNeighborhood">
                                    Bairro<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="personNeighborhood"
                                    name="personNeighborhood"
                                    value={personData.personNeighborhood}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personNeighborhood: e.target.value }));
                                        setErrors(prev => ({ ...prev, personNeighborhood: "" }));
                                    }}
                                    placeholder="Bairro"
                                    className={errors.personNeighborhood ? "border-error-500" : ""}
                                />
                                {errors.personNeighborhood && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personNeighborhood}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="personCity">
                                    Cidade<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="personCity"
                                    name="personCity"
                                    value={personData.personCity}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personCity: e.target.value }));
                                        setErrors(prev => ({ ...prev, personCity: "" }));
                                    }}
                                    placeholder="Cidade"
                                    className={errors.personCity ? "border-error-500" : ""}
                                />
                                {errors.personCity && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personCity}</p>
                                )}
                            </div>
                            <div className="md:col-span-4">
                                <Label htmlFor="personState">
                                    Estado<span className="text-error-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="personState"
                                    name="personState"
                                    value={personData.personState}
                                    onChange={(e) => {
                                        setPersonData(prev => ({ ...prev, personState: e.target.value }));
                                        setErrors(prev => ({ ...prev, personState: "" }));
                                    }}
                                    placeholder="Estado"
                                    className={errors.personState ? "border-error-500" : ""}
                                />
                                {errors.personState && (
                                    <p className="mt-1 text-sm text-error-500">{errors.personState}</p>
                                )}
                            </div>
                        </div>

                        {/* Dados Bancários */}
                        <div>
                            <h2 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-2">
                                Dados Bancários
                            </h2>

                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    A conta deve ter a mesma titularidade do CPF informado.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="accountType">
                                            Tipo de Conta<span className="text-error-500">*</span>
                                        </Label>
                                        <Select
                                            options={[
                                                { value: "checking", label: "Corrente" },
                                                { value: "savings", label: "Poupança" },
                                            ]}
                                            placeholder="Tipo de conta"
                                            value={personData.accountType}
                                            onChange={(value) => {
                                                setPersonData((prev) => ({ ...prev, accountType: value }));
                                                setErrors((prev) => ({ ...prev, bankAccountType: "" }));
                                            }}
                                            className={errors.bankAccountType ? "border-error-500" : ""}
                                        />
                                        {errors.bankAccountType && (
                                            <p className="mt-1 text-sm text-error-500">{errors.bankAccountType}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="bankNumber">
                                            Banco<span className="text-error-500">*</span>
                                        </Label>
                                        <SearchableSelect
                                            options={brazilianBanks.map(bank => ({
                                                value: bank.code,
                                                label: `${bank.code} - ${bank.name}`
                                            }))}
                                            value={personData.bankNumber}
                                            onChange={(value) => {
                                                setPersonData(prev => ({ ...prev, bankNumber: value }));
                                                if (errors.bankNumber) {
                                                    setErrors(prev => ({ ...prev, bankNumber: "" }));
                                                }
                                            }}
                                            placeholder="Selecione o banco"
                                            searchPlaceholder="Pesquisar banco..."
                                            error={!!errors.bankNumber}
                                        />
                                        {errors.bankNumber && (
                                            <p className="mt-1 text-sm text-error-500">{errors.bankNumber}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label htmlFor="agencyNumber">
                                            Número da Agência<span className="text-error-500">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            id="agencyNumber"
                                            name="agencyNumber"
                                            value={personData.agencyNumber}
                                            onChange={handlePersonChange}
                                            placeholder="Agência"
                                            className={errors.agencyNumber ? "border-error-500" : ""}
                                        />
                                        {errors.agencyNumber && (
                                            <p className="mt-1 text-sm text-error-500">{errors.agencyNumber}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="accountNumber">
                                            Número da Conta<span className="text-error-500">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            id="accountNumber"
                                            name="accountNumber"
                                            value={personData.accountNumber}
                                            onChange={handlePersonChange}
                                            placeholder="Conta"
                                            className={errors.accountNumber ? "border-error-500" : ""}
                                        />
                                        {errors.accountNumber && (
                                            <p className="mt-1 text-sm text-error-500">{errors.accountNumber}</p>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="accountDigit">
                                            Dígito da Conta<span className="text-error-500">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            id="accountDigit"
                                            name="accountDigit"
                                            value={personData.accountDigit}
                                            onChange={handlePersonChange}
                                            placeholder="Dígito"
                                            className={errors.accountDigit ? "border-error-500" : ""}
                                        />
                                        {errors.accountDigit && (
                                            <p className="mt-1 text-sm text-error-500">{errors.accountDigit}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 mt-4">
                            <Checkbox
                                className="w-5 h-5 mt-1"
                                checked={personData.termsAccepted}
                                onChange={(checked) => setPersonData(prev => ({ ...prev, termsAccepted: checked }))}
                            />
                            <div>
                                <p className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                    Confirmo que as informações acima são verdadeiras.
                                </p>
                                {errors.termsAccepted && (
                                    <p className="mt-1 text-sm text-error-500">{errors.termsAccepted}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-start gap-3 mt-4">
                            <Checkbox
                                className="w-5 h-5 mt-1"
                                checked={personData.privacyTermsAccepted}
                                onChange={(checked) => setPersonData(prev => ({ ...prev, privacyTermsAccepted: checked }))}
                            />
                            <div>
                                <p className="text-sm font-normal text-gray-700 dark:text-gray-400">
                                    Li e aceito os termos de uso, termos de licença de uso de software & a política de conteúdo da Sellyo.
                                </p>
                                {errors.privacyTermsAccepted && (
                                    <p className="mt-1 text-sm text-error-500">{errors.privacyTermsAccepted}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 transition bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        Voltar
                    </button>
                    <button
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                        Finalizar
                    </button>
                </div>
            </div>
        );
    };

    // Renderizar o formulário com base no passo atual
    return (
        <div className="flex flex-col flex-1 py-6">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div>
                    <div className="mb-5 pt-4 sm:mb-8">
                        <img
                            src="/images/logo/logo-icon.svg"
                            alt="Logo"
                            className="mx-auto"
                            width={48}
                            height={48}
                        />
                        <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                            Vamos trabalhar juntos
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Complete o onboarding para começar a usar a Sellyo
                        </p>
                    </div>
                    <div className="pb-8">
                        {apiError && (
                            <div className="p-3 mb-5 text-sm text-white rounded-lg bg-error-500">
                                {apiError}
                            </div>
                        )}

                        {alert.show && (
                            <div className="mb-5">
                                <Alert
                                    variant={alert.variant as "success" | "error" | "warning" | "info"}
                                    title={alert.title}
                                    message={alert.message}
                                />
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {isMounted && currentStep === 1 && renderStep1()}
                            {isMounted && currentStep === 2 && accountType === "cnpj" && renderStep2Cnpj()}
                            {isMounted && currentStep === 2 && accountType === "cpf" && renderStep2Cpf()}
                            {isMounted && currentStep === 3 && accountType === "cnpj" && renderStep3Cnpj()}
                            {isMounted && currentStep === 3 && accountType === "cpf" && renderStep3Cpf()}
                            {isMounted && currentStep === 4 && accountType === "cnpj" && renderStep4Cnpj()}
                        </form>

                        {/* Selo BaaS Asaas: a conta de pagamento do produtor é aberta no Asaas */}
                        <AsaasFinancialNotice
                            variant="auto"
                            className="mt-8"
                            notice="Sua conta de pagamento e os serviços financeiros (recebimentos, saldo e saques) são prestados pelo Asaas, instituição de pagamento autorizada pelo Banco Central do Brasil."
                            supportLabel="Suporte Asaas:"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
